import { useState, useEffect, useRef, useCallback } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import logo1 from "../Components/Assets/tlogo.png";
import logo from "../Components/Assets/textLogo.png";
import { useLocation } from "react-router-dom";

const BP_MOBILE = 768;
const BP_TABLET = 1024;

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const path = location?.pathname;
  const drawerRef = useRef(null);

  const [screenSize, setScreenSize] = useState("desktop");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarScreen, setSidebarScreen] = useState("main");

  /* ── measure screen size ── */
  const getSize = useCallback(() => {
    const w = window.innerWidth;
    if (w <= BP_MOBILE) return "mobile";
    if (w <= BP_TABLET) return "tablet";
    return "desktop";
  }, []);

  useEffect(() => {
    const update = () => setScreenSize(getSize());
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [getSize]);

  /* ── close mobile drawer on navigation ── */
  useEffect(() => {
    if (screenSize === "mobile") {
      setMobileOpen(false);
    }
  }, [path, screenSize]);

  /* ── close mobile drawer on outside click ── */
  useEffect(() => {
    if (screenSize !== "mobile" || !mobileOpen) return;

    let timerId;
    const handler = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        setMobileOpen(false);
      }
    };
    timerId = setTimeout(() => {
      document.addEventListener("mousedown", handler);
      document.addEventListener("touchstart", handler);
    }, 50);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [mobileOpen, screenSize]);

  /* ── toggle: only mobile drawer opens/closes; tablet is no-op ── */
  const toggleSidebar = () => {
    if (screenSize === "mobile") {
      setMobileOpen((v) => !v);
    }
    // tablet and desktop: no-op — sidebar is always visible and full-width
  };

  const isMobile = screenSize === "mobile";
  const isTablet = screenSize === "tablet";
  const isDesktop = screenSize === "desktop";

  const siderWidth = sidebarScreen === "main" ? 200 : 190;

  /* ── Logo ── */
  const LogoBlock = () => {
    const isCompact = siderWidth <= 140;
    return (
      <div
        style={{
          flexShrink: 0,
          padding: isCompact ? "18px 8px" : "14px 18px",
          display: "flex",
          flexDirection: isCompact ? "column" : "row",
          alignItems: "center",
          gap: isCompact ? 6 : 10,
          background: "#1c1078",
          minHeight: isCompact ? 90 : 62,
        }}
      >
        <img
          src={logo1}
          alt="Logo"
          style={{ width: 30, height: 30, objectFit: "contain" }}
        />
        <img
          src={logo}
          alt="Logo"
          style={{
            height: 22,
            width: 120,
            // maxWidth: 120,
            objectFit: "contain",
          }}
        />
      </div>
    );
  };

  return (
    <div
      style={{
        height: "100vh",
        overflow: "hidden",
        background: "#f0f2f5",
        display: "flex",
        flexDirection: "row",
      }}
    >
      {/* ══════════ MOBILE — slide-in drawer ══════════ */}
      {isMobile && (
        <>
          {/* Dim backdrop */}
          <div
            onClick={() => setMobileOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(2px)",
              zIndex: 997,
              opacity: mobileOpen ? 1 : 0,
              pointerEvents: mobileOpen ? "auto" : "none",
              transition: "opacity 0.25s",
            }}
          />

          {/* Drawer panel */}
          <div
            ref={drawerRef}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              bottom: 0,
              width: siderWidth,
              zIndex: 998,
              display: "flex",
              flexDirection: "column",
              transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
              transition:
                "transform 0.28s cubic-bezier(0.4,0,0.2,1), width 0.25s ease",
              boxShadow: mobileOpen ? "4px 0 24px rgba(0,0,0,0.3)" : "none",
              overflow: "hidden",
            }}
          >
            <div style={{ background: "#2d1b69", flexShrink: 0 }}>
              <LogoBlock />
            </div>
            <div
              style={{
                background: "#ffffff",
                flex: 1,
                minHeight: 0,
                overflowY: "hidden",
                overflowX: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Sidebar onScreenChange={setSidebarScreen} />
            </div>
          </div>
        </>
      )}

      {/* ══════════ TABLET — fixed, always visible ══════════ */}
      {isTablet && (
        <div
          style={{
            width: siderWidth,
            flexShrink: 0,
            background:
              "linear-gradient(160deg, #2d1b69 0%, #4c3494 45%, #7c5cbf 80%, #b8a4e8 100%)",
            position: "sticky",
            top: 0,
            height: "100vh",
            overflow: "hidden",
            zIndex: 100,
            transition: "width 0.25s ease",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <LogoBlock />
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Sidebar onScreenChange={setSidebarScreen} />
          </div>
        </div>
      )}

      {/* ══════════ DESKTOP — fixed, always visible ══════════ */}
      {isDesktop && (
        <div
          style={{
            width: siderWidth,
            flexShrink: 0,
            background:
              "linear-gradient(160deg, #2d1b69 0%, #4c3494 45%, #7c5cbf 80%, #b8a4e8 100%)",
            position: "sticky",
            top: 0,
            height: "100vh",
            overflow: "hidden",
            zIndex: 100,
            transition: "width 0.25s ease",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <LogoBlock />
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Sidebar onScreenChange={setSidebarScreen} />
          </div>
        </div>
      )}

      {/* ══════════ Main content area ══════════ */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
          height: "100vh",
          overflow: "hidden",
        }}
      >
        <Header
          toggleSidebar={toggleSidebar}
          screenSize={screenSize}
          mobileOpen={mobileOpen}
        />
        <div
          style={{
            margin: isMobile ? "8px" : "10px",
            padding: isMobile ? "8px" : "10px",
            backgroundColor: "#fff",
            borderRadius: 10,
            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
            overflowY: "auto",
            overflowX: "hidden",
            flex: 1,
            minHeight: 0,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
